import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import ProgressButton from "../components/ProgressButton";
import bookingApi from "../service/bookingApi";
import { format } from "date-fns";
import { ClipLoader } from "react-spinners";
import { ToastContext } from "../contexts/ToastProvider";

const Wrapper = styled.div`
  gap: 10px;
  justify-content: center;
  padding: 10px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  margin-top: 10vh;
  align-items: center;
`;

const TimeSlotWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  max-width: 75%;
`;

const TimeSlotButton = styled.button`
  padding: 10px 15px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 5px;
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.primary : theme.colors.white};
  color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.white : theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.primaryDark : theme.colors.primary};
    color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.white : theme.colors.white};
  }
`;

function Booking({ onNext, onBack }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useContext(ToastContext);

  const handleDateChange = (date) => {
    if (!date) return;
    // Reset any prior time selection + clear stale slots so nothing from the previous
    // date lingers (and a stale time can't be carried into the booking).
    setSelectedTime(null);
    setBookedSlots([]);
    setLoading(true);
    setSelectedDate(format(date, "yyyy-MM-dd"));
  };

  useEffect(() => {
    if (selectedDate) {
      setLoading(true);
      bookingApi
        .getBookingId(selectedDate, selectedDate)
        .then((response) => {
          setBookedSlots(response || []);
        })
        .catch((error) => {
          console.error("Error fetching booked slots:", error);
          setBookedSlots([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedDate]);

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleNext = () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Vælg venligst en dato og et tidspunkt.");
      return;
    }
    onNext({ date: selectedDate, time: selectedTime });
  };

  // Backend returns the final bookable start times (15-min grid, each a free 30-min
  // window, aggregated across all staff calendars, past times already filtered).
  const availableTimes = bookedSlots.flatMap((slot) => slot.times || []);

  return (
    <Wrapper>
      <h1>Vælg en tid</h1>
      <p>Vælg en dato og et tidspunkt for din booking.</p>
      <Calendar onChange={handleDateChange} minDate={new Date()} />

      {selectedDate && loading && (
        <div style={{ marginTop: "20px" }}>
          <ClipLoader color="#018D66" size={32} />
        </div>
      )}

      {selectedDate && !loading && availableTimes.length > 0 && (
        <TimeSlotWrapper>
          {availableTimes.map((time, index) => (
            <TimeSlotButton
              key={index}
              isSelected={selectedTime === time}
              onClick={() => handleTimeSelect(time)}
            >
              {time}
            </TimeSlotButton>
          ))}
        </TimeSlotWrapper>
      )}

      {selectedDate && !loading && availableTimes.length === 0 && (
        <p style={{ color: "#018D66", marginTop: "20px" }}>
          Der er ingen ledige tider på den valgte dato.
        </p>
      )}

      <div style={{ marginTop: "20px" }}>
        <ProgressButton text="Tilbage" onClick={onBack} />
        <ProgressButton text="Videre" onClick={handleNext} />
      </div>
    </Wrapper>
  );
}

export default Booking;
